import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useHistory } from "react-router";
import { ProjectContext } from "context/ProjectProvider";
import {
  FeatureLogNavContext,
  FeatureLogNavFeature,
  FeatureLogNavSnapshot
} from "context/FeatureLogNavProvider";
import { FeatureTypes } from "entity-app/constants";
import { TAuditChangeLog } from "change-events/change-event-polling";

type Props = {
  feature: FeatureLogNavFeature;
  projectId: string;
  currentId: string;
  /** Details pages whose id segment is not the last path part. */
  toPath?: (id: string) => string;
};

export type FeatureLogNavReaderResult = {
  visible: boolean;
  currentIndex: number; // zero-based, -1 if not in snapshot
  total: number;
  prevId: string | null;
  nextId: string | null;
  goPrev: () => void;
  goNext: () => void;
};

const FEATURE_TYPE_BY_FEATURE: Record<FeatureLogNavFeature, FeatureTypes> = {
  materials: FeatureTypes.MATERIALS,
  submittals: FeatureTypes.SUBMITTALS,
  "bid-packages": FeatureTypes.BID_PACKAGE,
  "design-packages": FeatureTypes.DESIGN_PACKAGE
};

const FEATURE_TYPE_LABEL_BY_FEATURE: Record<FeatureLogNavFeature, string> = {
  materials: "MATERIALS",
  submittals: "SUBMITTALS",
  "bid-packages": "BID_PACKAGE",
  "design-packages": "DESIGN_PACKAGE"
};

const featureTypeFor = (feature: FeatureLogNavFeature) =>
  FEATURE_TYPE_BY_FEATURE[feature];

const featureTypeLabelFor = (feature: FeatureLogNavFeature) =>
  FEATURE_TYPE_LABEL_BY_FEATURE[feature];

/**
 * Details-page side of the prev/next navigator. Reads the ordered ID list
 * captured by the list page from FeatureLogNavContext, then keeps it in sync
 * with server-driven create/delete events via the existing eventLogs poll.
 */
export const useFeatureLogNavReader = ({
  feature,
  projectId,
  currentId,
  toPath
}: Props): FeatureLogNavReaderResult => {
  const history = useHistory();
  const { getSnapshot, appendIds, removeIds } =
    useContext(FeatureLogNavContext);
  const { eventLogs } = useContext(ProjectContext);

  const snapshot: FeatureLogNavSnapshot | null = useMemo(
    () => getSnapshot(feature, projectId),
    // depend on the snapshot's mutating identity so this recomputes whenever
    // append/remove fires
    [getSnapshot, feature, projectId]
  );

  const previousEventLogs = useRef(eventLogs);
  useEffect(() => {
    if (!eventLogs?.length || previousEventLogs.current === eventLogs) {
      previousEventLogs.current = eventLogs;
      return;
    }

    const featureTypeId = featureTypeFor(feature);
    const featureTypeLabel = featureTypeLabelFor(feature);

    const featureLogs = eventLogs.filter(
      (log) =>
        (log.info?.feature_type === featureTypeLabel ||
          log.info?.feature_type_ids?.includes(featureTypeId)) &&
        log.data_source === "feature_instance"
    );

    if (featureLogs.length) {
      const deletedIdsNested = featureLogs
        .filter((log) => ["delete", "bulk_delete"].includes(log.change_type))
        .map((log: TAuditChangeLog) => log.info.feature_instance_ids ?? []);
      if (deletedIdsNested.length) {
        const deletedIds: string[] = Array.from(
          new Set<string>(([] as string[]).concat(...deletedIdsNested))
        );
        if (deletedIds.length) {
          removeIds(feature, projectId, deletedIds);
        }
      }

      const createdIdsNested = featureLogs
        .filter((log) => !["delete", "bulk_delete"].includes(log.change_type))
        .map((log: TAuditChangeLog) => log.info.feature_instance_ids ?? []);
      if (createdIdsNested.length) {
        const createdIds: string[] = Array.from(
          new Set<string>(([] as string[]).concat(...createdIdsNested))
        );
        if (createdIds.length) {
          appendIds(feature, projectId, createdIds);
        }
      }
    }

    previousEventLogs.current = eventLogs;
  }, [eventLogs, feature, projectId, appendIds, removeIds]);

  const navigateTo = useCallback(
    (id: string | null) => {
      if (!id) return;
      history.push({
        pathname: toPath?.(id) ?? `/project/${projectId}/${feature}/${id}`,
        state: history.location.state
      });
    },
    [history, projectId, feature, toPath]
  );

  return useMemo<FeatureLogNavReaderResult>(() => {
    if (!snapshot || snapshot.orderedIds.length === 0) {
      return {
        visible: false,
        currentIndex: -1,
        total: 0,
        prevId: null,
        nextId: null,
        goPrev: () => {},
        goNext: () => {}
      };
    }

    const total = snapshot.orderedIds.length;
    const currentIndex = snapshot.orderedIds.indexOf(currentId);
    const prevId =
      currentIndex > 0 ? snapshot.orderedIds[currentIndex - 1] : null;
    const nextId =
      currentIndex >= 0 && currentIndex < total - 1
        ? snapshot.orderedIds[currentIndex + 1]
        : null;

    return {
      visible: true,
      currentIndex,
      total,
      prevId,
      nextId,
      goPrev: () => navigateTo(prevId),
      goNext: () => navigateTo(nextId)
    };
  }, [snapshot, currentId, navigateTo]);
};

export default useFeatureLogNavReader;
